import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext } from 'react';
import { Toaster } from 'react-hot-toast';

// API 전역 컨텍스트 타입
interface ApiContextType {
  queryClient: QueryClient;
}

// 컨텍스트 생성
const ApiContext = createContext<ApiContextType | undefined>(undefined);

// API 컨텍스트 훅
export function useApiContext() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApiContext must be used within an ApiProvider');
  }
  return context;
}

// QueryClient 설정
const defaultQueryClientOptions = {
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5분 기본 캐싱
      refetchOnWindowFocus: 'always' as const,
      refetchOnMount: true,
    },
    mutations: {
      retry: 0, // 뮤테이션은 기본적으로 재시도 하지 않음
    },
  },
};

interface ApiProviderProps {
  children: ReactNode;
  queryClient?: QueryClient;
}

/**
 * API 프로바이더 컴포넌트
 */
export function ApiProvider({
  children,
  queryClient = new QueryClient(defaultQueryClientOptions),
}: ApiProviderProps) {
  // QueryClient 컨텍스트 값
  const contextValue: ApiContextType = {
    queryClient,
  };

  return (
    <ApiContext.Provider value={contextValue}>
      <QueryClientProvider client={queryClient}>
        {children}

        {/* 토스트 메시지 컴포넌트 */}
        <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: 'white',
              color: '#333',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              padding: '16px',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: 'white',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: 'white',
              },
            },
          }}
        />
      </QueryClientProvider>
    </ApiContext.Provider>
  );
}

export default ApiProvider;
